import { CANON } from '@/config/canon';
import type { DocumentDef } from './types';

/**
 * Environmental storytelling documents. Placeholder canon: interrupted routines, failed emergency
 * measures, shortages, and the gap between official guidance and what residents see.
 */
export const DOCUMENTS: DocumentDef[] = [
  {
    id: 'doc_notice',
    x: 9.2,
    z: 9.6,
    y: 1.5,
    yaw: Math.PI / 2,
    style: 'official',
    title: 'Notice to residents',
    body: [
      'CITY EMERGENCY MANAGEMENT — NOTICE TO RESIDENTS',
      '',
      CANON.officialGuidance,
      '',
      `Do not approach persons showing signs of ${CANON.officialDisasterName}.`,
      'Do not attempt to travel by private vehicle. Await further instruction.',
      '',
      'Posted 48 hours ago. No further instruction has been posted.',
    ].join('\n'),
  },
  {
    id: 'doc_door_note',
    x: 13.6,
    z: 17.2,
    y: 1.4,
    yaw: Math.PI,
    style: 'handwritten',
    title: 'Note taped to the door',
    body: [
      'Went to the school with the kids. The phones are down.',
      'If you read this — don’t wait for us. Go while the crossing is open.',
      '',
      '— M.',
    ].join('\n'),
  },
  {
    id: 'doc_transit',
    x: 58.6,
    z: 30.4,
    y: 1.6,
    yaw: 0,
    style: 'official',
    title: 'Transit notice',
    body: [
      'ROUTE 4 — SERVICE SUSPENDED',
      '',
      'Vehicle disabled. Do not board.',
      'Passengers are directed to Route 7.',
      '',
      'Someone has written underneath in marker: 7 IS GONE TOO.',
    ].join('\n'),
  },
  {
    id: 'doc_rationing',
    x: 41.6,
    z: 40.2,
    y: 1.2,
    style: 'print',
    title: 'Sign on the counter',
    body: [
      'ONE PACK PER HOUSEHOLD. NO EXCEPTIONS.',
      'We are out of the blue ones. Please stop asking.',
      'Cash only — the terminals have been down since this morning.',
      '',
      'The register drawer is open and empty.',
    ].join('\n'),
  },
  {
    id: 'doc_transcript',
    x: 70.6,
    z: 34.15,
    y: 1.2,
    style: 'handwritten',
    title: 'Attendant’s notebook',
    body: [
      'Radio, 21:40 — repeating:',
      '"…crossing remains open until first light. At first light the district east of the rail line will be sealed."',
      '',
      '"If you are east of the rail line, move now. Do not wait for transport."',
      '',
      'They said the same thing at 19:00. They did not say what happens after.',
    ].join('\n'),
  },
  // The three notices below follow the shape of public-domain civil-emergency messaging
  // (shelter-in-place guidance, an emergency alert transcript, a water advisory), rewritten in
  // the project's voice. Nothing is quoted.
  {
    id: 'doc_shelter',
    x: 31.06,
    z: 36,
    y: 1.5,
    yaw: Math.PI / 2,
    style: 'official',
    title: 'Shelter-in-place instructions',
    body: [
      `CITY EMERGENCY MANAGEMENT — ${CANON.officialDisasterName.toUpperCase()}`,
      'SHELTER IN PLACE. EFFECTIVE UNTIL FURTHER NOTICE.',
      '',
      '1. Go indoors. Bring pets. Do not go looking for family members who are elsewhere; they are being told the same thing.',
      '2. Close and lock every door and window. Switch off fans, heating and air conditioning.',
      '3. Choose one interior room with the fewest windows. Take water, a torch and a radio.',
      `4. Keep the radio on. Instructions will be repeated on the emergency channel.`,
      '5. Do not open the door to anyone showing signs of exposure, whoever they are.',
      '',
      'Someone has underlined the last line twice.',
    ].join('\n'),
  },
  {
    id: 'doc_alert',
    x: 65.4,
    z: 74.6,
    y: 1.0,
    style: 'print',
    title: 'Alert transcript (printout)',
    body: [
      'EMERGENCY ALERT — CIVIL EMERGENCY MESSAGE',
      'Issued at the request of City Emergency Management. Repeats every fifteen minutes.',
      '',
      `A civil emergency has been declared for ${CANON.districtName} and the blocks east of the rail line.`,
      `Persons in the affected area: ${CANON.crossingLabel} is the only exit still staffed. It closes at first light.`,
      'Move on foot. Vehicles are being stopped at the barrier line. Carry identification and water.',
      'Do not approach persons who are unresponsive, disoriented or aggressive. Report them to the checkpoint.',
      '',
      'Stay tuned to this channel. Further instructions will follow.',
      '',
      'The page is timestamped 23:10. There is no later page.',
    ].join('\n'),
  },
  {
    id: 'doc_water',
    x: 58.06,
    z: 62.5,
    y: 1.5,
    yaw: Math.PI / 2,
    style: 'official',
    title: 'Water advisory',
    body: [
      'WATER SUPPLY ADVISORY — EAST DISTRICT',
      '',
      'Do not drink water from the tap, including for cooking or brushing teeth, until this notice is withdrawn.',
      'Bottled water is being issued at the plaza aid station, one case per household, while stocks last.',
      'If you must use tap water, bring it to a rolling boil for one full minute and let it cool covered.',
      '',
      'Aid station hours: 06:00 to 20:00.',
      '',
      'The aid station is the tent behind you. It is empty.',
    ].join('\n'),
  },
];
